import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import IconTile from '../common/IconTile';
import { CHAT_CALLS_URL } from '../../common/constants';
import { callClock } from '../../common/callTimes';

// Webflow section_layout308 #2: "Community calls & events".
const CALL_ICONS = [GroupsOutlinedIcon, CodeOutlinedIcon, ForumOutlinedIcon, PublicOutlinedIcon];

// Start times, positionally aligned with the `calls` array in every locale
// (same convention as CALL_ICONS). Deliberately not in the translation files,
// which would store each time three times over; callClock derives the other
// clock. Confirmed against the pinned OCL Chat topic 2026-09-18
// (ocl_online#147); the squad call's 14:00 UTC is from its OpenMRS wiki page
// (ocl_online#260).
const CALL_TIMES = [{ eastern: '08:00' }, { utc: '14:00' }, { eastern: '10:00' }, { eastern: '09:00' }];

const getCalls = t =>
  t('home.community_calls.calls', { returnObjects: true }).map((call, i) => {
    const { utc, eastern } = callClock(CALL_TIMES[i]);
    return {
      ...call,
      icon: CALL_ICONS[i],
      utc: t('common.time_utc', { time: utc }),
      eastern: t('common.time_eastern', { time: eastern }),
    };
  });

const MetaRow = ({ parts, bold }) => (
  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
    {parts.map((part, i) => (
      <React.Fragment key={part}>
        {i > 0 && <Typography variant="body2" sx={{ color: 'surface.contrastText' }}>·</Typography>}
        <Typography variant="body2" sx={{ color: 'surface.contrastText', fontWeight: bold ? 600 : 400 }}>
          {part}
        </Typography>
      </React.Fragment>
    ))}
  </Box>
);

const CallCard = ({ icon, title, text, frequency, utc, eastern }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
    <IconTile icon={icon} sx={{ mb: 2 }} />
    <Typography variant="h5" component="h3" sx={{ color: 'surface.dark', mb: 1.5 }}>
      {title}
    </Typography>
    <Typography variant="body1" sx={{ color: 'surface.contrastText', mb: 2 }}>
      {text}
    </Typography>
    {/* UTC leads — this is a global community — with the Eastern time the
        calls are actually scheduled against on the line beneath it. */}
    <Box sx={{ mt: 'auto' }}>
      <MetaRow parts={[frequency, utc]} bold />
      <MetaRow parts={[eastern]} />
    </Box>
  </Box>
);

const CommunityCalls = () => {
  const { t } = useTranslation();
  return (
    <Box id="community-calls" component="section" aria-labelledby="community-calls-heading" sx={{ backgroundColor: 'surface.n96', scrollMarginTop: '80px' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: { xs: 5, md: 8 } }}>
          <Typography sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
            {t('home.community_calls.eyebrow')}
          </Typography>
          <Typography id="community-calls-heading" variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '2rem', md: '2.5rem' }, mb: 1 }}>
            {t('home.community_calls.heading')}
          </Typography>
          {/* Zoom rooms and running-notes docs live in one pinned OCL Chat
              topic, not on this page: a public raw Zoom link invites bot
              joins, and Chat keeps the details in one place to maintain. */}
          <Typography sx={{ color: 'surface.contrastText' }}>
            <Trans
              i18nKey="home.community_calls.text"
              components={{
                chat: <Link href={CHAT_CALLS_URL} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }} />,
              }}
            />
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 5, md: 4 } }}>
          {getCalls(t).map(call => (
            <CallCard key={call.title} {...call} />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default CommunityCalls;
